import React, { ReactNode } from 'react';
import cx from 'classnames';
import styles from './Tabs.module.scss';
import type { TabsViewProps } from './Tabs.types';
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import TabItem from './TabItem';
import TabContent from './TabContent';

/**
 * Recursively walks the tree and collects any wrapper elements
 * that contain components with the given displayName (e.g. 'TabItem').
 * This ensures we preserve layout wrappers (like divs).
 */
const getBranchesWithComponent = (
  children: ReactNode,
  matchComponent: React.ComponentType<any>
): ReactNode[] => {
  const branches: ReactNode[] = [];

  React.Children.forEach(children, (child: any) => {
    if (!child) return;

    const isMatch = child.type === matchComponent;

    if (isMatch) {
      branches.push(child);
    } else if (child.props?.children) {
      const nested = getBranchesWithComponent(
        child.props.children,
        matchComponent
      );

      if (nested.length > 0) {
        // Clone the wrapper and keep the matched nested content
        branches.push(
          React.cloneElement(child, { key: child.key }, nested)
        );
      }
    }
  });

  return branches;
};


const Tabs = (props: TabsViewProps) => {
  const {
    className,
    children,
    type = 'Horizontal',
  } = props;

  const variantProps = useBuildVariantProps(
    'tabs-header',
    {
    }
  );

  // Filter and separate Tab and TabPanel components
  const tabs = getBranchesWithComponent(children, TabItem);
  const panels = getBranchesWithComponent(children, TabContent);

  return (
    <div 
      className={cx(styles.tabs, styles[type.toLowerCase()], className)}
      {...variantProps}
    >
      <div className={styles.tabsHeader} role="tablist">
        {tabs}
      </div>
      <div className={styles.tabsContent}>
        {panels}
      </div>
    </div>
  );
};
export default Tabs;
